import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Stack,
  Button,
  Fade,
  ClickAwayListener,
  useScrollTrigger,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";

const links = [
  { label: "Projects", href: "#projects" },
  { label: "GitHub", href: "https://github.com/muriminicholas" },
  { label: "Resume", href: "/resume.pdf" },
  { label: "Contact", href: "#contact" },
];

export default function QuickLinksFloating() {
  // 1. ALL HOOKS MUST BE HERE AT THE TOP LEVEL
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  // MUI Hook to detect scrolling
  const scrolled = useScrollTrigger({ 
    disableHysteresis: true, 
    threshold: 50 
  });

  // 2. USE EFFECTS FOR LOGIC
  
  // Sync the "Pill" vs "Circle" shape with scroll status
  useEffect(() => {
    setIsExpanded(!scrolled);
  }, [scrolled]);

  // Handle visibility and auto-closing logic
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      
      // Hide button if scrolled deep into the page (past hero)
      setVisible(window.scrollY < heroHeight - 100);

      // Auto-close menu if user scrolls while it's open
      if (open && window.scrollY > 50) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [open]);

  // 3. HANDLERS (Plain functions, no hooks inside here!)
  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
    // If user clicks while it's a circle, force it to expand
    if (!isExpanded) setIsExpanded(true);
  };

  const iconSizes = { xs: "40px", sm: "44px", md: "48px" };

  return (
    <Fade in={visible}>
      <Box sx={{zIndex:1000}}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            
            <IconButton
              onClick={handleToggle}
              sx={{
                borderRadius: isExpanded ? "14px" : "50%",
                width: isExpanded ? "auto" : iconSizes,
                height: iconSizes, 
                px: isExpanded ? 2.5 : 0,
                display: "flex",
                gap: isExpanded ? 1.5 : 0,
                background: "linear-gradient(135deg, #078d3a, #eed709)",
                color: "#ffffff",
                boxShadow: "0 8px 25px rgba(7, 141, 58, 0.3)",
                transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                "&:hover": { transform: "scale(1.05)" },
              }}
            >
              <LinkIcon fontSize="small" />
              {isExpanded && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "1px",
                    color: "#fff",
                    fontSize: { xs: "0.7rem", md: "0.75rem" },
                    whiteSpace: "nowrap",
                  }}
                >
                  QUICK LINKS
                </Typography>
              )}
            </IconButton>

            {/* Menu Panel */}
            <Fade in={open}>
              <Box
                sx={{
                  position: "absolute",
                  bottom: "120%", 
                  right: 0,
                  width: 200,
                  p: 2,
                  borderRadius: "16px",
                  background: "rgba(15, 23, 42, 0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
                }}
              >
                <Stack spacing={1}>
                  {links.map((link, i) => (
                    <Button
                      key={i}
                      href={link.href}
                      fullWidth
                      sx={{ color: "white", justifyContent: "space-between", textTransform: "none" }}
                      endIcon={<ArrowOutwardIcon sx={{ fontSize: 14 }} />}
                    >
                      {link.label}
                    </Button>
                  ))}
                </Stack>
              </Box>
            </Fade>

          </Box>
        </ClickAwayListener>
      </Box>
    </Fade>
  );
}
